import Joyride, { CallBackProps, EVENTS, STATUS } from 'react-joyride';
import { Button } from '../buttons/Button.tsx';
import API from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.ts';

const Tooltip = ({
    continuous,
    index,
    size,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
}) => (
    <div
        {...tooltipProps}
        className="max-w-[360px] rounded-2xl bg-white p-4 shadow-sm"
    >
        <div className="flex justify-between">
            {step.title && <h5 className="mb-1">{step.title}</h5>}
            <span className="font-display text-sm font-bold text-ink-800">
                {index + 1}/{size}
            </span>
        </div>
        <p className="text-ink-800">{step.content}</p>
        {/* Buttons */}
        <div className="mt-3 flex space-x-3">
            {index > 0 && (
                <Button color="product" variant="secondary" {...backProps}>
                    Zpět
                </Button>
            )}
            {continuous && (
                <Button
                    color="product"
                    variant="primary"
                    {...primaryProps}
                    className="w-full"
                >
                    Další
                </Button>
            )}
        </div>
    </div>
);

interface DashboardJoyrideProps {
    run: boolean;
}

export default function DashboardJoyride({ run }: DashboardJoyrideProps) {
    const { user, userHasFullLicense } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'DashboardJoyride',
    });

    const walktourSteps = [
        {
            target: '.tour--recommended-lectures',
            title: 'Doporučená cvičení',
            content:
                'V této sekci naleznete cvičení vhodná pro různé části školního dne.',
            disableBeacon: true,
            placement: 'top',
        },
        {
            target: '.tour--categories-nav',
            title: 'Kategorie',
            content:
                'Cvičení můžete procházet i na základě jednotlivých témat.',
            disableBeacon: true,
            placement: 'top',
        },
    ];

    // only if user has no active subscription
    if (!userHasFullLicense) {
        walktourSteps.push({
            target: '.tour--buy-full-version',
            title: 'Plná verze aplikace',
            // content: 'Po 7 dnech vám vyprší zkušební verze aplikace a zůstanou vám pouze základní funkce.',
            // content:
            //     'Zakupte si plnou verzi aplikace a získejte neomezený přístup ke všem cvičením a funkcím.',
            content: 'Spusťte si plnou verzi na 14 dní zdarma. Po skončení se aplikace vrátí do základní verze. Pokud chcete pokračovat, můžete plnou verzi objednat přímo v aplikaci.',
            disableBeacon: true,
            placement: 'right',
        });
    }

    walktourSteps.push({
        target: '.tour--custom-plan',
        title: 'Začněte zde',
        content: 'Sestavte si plán na míru.',
        disableBeacon: true,
        placement: 'bottom',
    });

    return (
        <Joyride
            run={run}
            steps={walktourSteps}
            continuous={true}
            hideCloseButton={true}
            scrollToFirstStep={false}
            showProgress={true}
            showSkipButton={false}
            styles={{
                spotlight: {
                    borderRadius: 24,
                },
                options: {
                    width: 360,
                    zIndex: 10000,
                },
            }}
            spotlightPadding={16}
            tooltipComponent={Tooltip}
            locale={{
                back: 'Zpět',
                close: 'Zavřít',
                last: 'Hotovo',
                next: 'Další',
                open: 'Otevřít',
                skip: 'Přeskočit',
            }}
            callback={({
                action,
                lifecycle,
                index,
                origin,
                status,
                type,
            }: CallBackProps) => {
                if (status === STATUS.FINISHED && type === EVENTS.TOUR_END) {
                    // Update the user's profile to indicate that they have completed the walkthrough
                    // This will prevent the walkthrough from showing again
                    API.patch('/user/features/walkthroughCompleted');
                }
            }}
        />
    );
}
